/**
 * Lightbox video (sostituisce lightGallery).
 *
 * Legge lo stesso markup usato finora:
 *   <a class="js-video-lightbox"
 *      data-video='{"source":[{"src":"…mp4","type":"video/mp4"}],"attributes":{…}}'
 *      data-poster="…jpg">
 *
 * Overlay in un <div> fisso (non <dialog>) appeso al <body>, fuori da
 * #smooth-wrapper, così funziona anche su Safari/iOS datati e non risente
 * delle trasformazioni di ScrollSmoother.
 */

import { ScrollSmoother } from 'gsap/ScrollSmoother';

let overlay = null;
let video = null;
let closeButton = null;
let lastFocus = null;
let isOpen = false;

const parseSource = (trigger) => {
	try {
		const data = JSON.parse(trigger.getAttribute('data-video'));
		const source = data && data.source && data.source[0];
		return source && source.src ? source : null;
	} catch (e) {
		return null;
	}
};

const pauseScrolling = (paused) => {
	// fullpage.js (home, how we work, who we are)
	if (window.fullpage_api && window.fullpage_api.setAllowScrolling) {
		window.fullpage_api.setAllowScrolling(!paused);
		window.fullpage_api.setKeyboardScrolling(!paused);
	}

	// ScrollSmoother (pagine normali)
	const smoother = ScrollSmoother.get();
	if (smoother) smoother.paused(paused);

	// html/body overflow hidden: copre i casi senza smoother
	document.documentElement.classList[paused ? 'add' : 'remove']('video-lightbox-open');
};

const onKeydown = (e) => {
	if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
		e.preventDefault();
		close();
	} else if (e.key === 'Tab' || e.keyCode === 9) {
		// tiene il focus dentro il lightbox
		const focusables = [closeButton, video];
		const index = focusables.indexOf(document.activeElement);
		const next = e.shiftKey ? index - 1 : index + 1;
		e.preventDefault();
		focusables[(next + focusables.length) % focusables.length].focus();
	}
};

const build = (protectVideo) => {
	overlay = document.createElement('div');
	overlay.className = 'video-lightbox';
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	overlay.setAttribute('aria-label', 'Video');
	overlay.setAttribute('aria-hidden', 'true');

	closeButton = document.createElement('button');
	closeButton.type = 'button';
	closeButton.className = 'video-lightbox__close';
	closeButton.setAttribute('aria-label', 'Close');

	video = document.createElement('video');
	video.className = 'video-lightbox__video';
	video.controls = true;
	video.preload = 'auto';
	// attributi (non proprietà) per l'autoplay inline su iOS
	video.setAttribute('playsinline', '');
	video.setAttribute('webkit-playsinline', '');
	video.setAttribute('tabindex', '0');
	protectVideo(video);

	overlay.appendChild(video);
	overlay.appendChild(closeButton);
	document.body.appendChild(overlay);

	closeButton.addEventListener('click', close);
	overlay.addEventListener('click', (e) => {
		// click sullo sfondo nero, non sul video
		if (e.target === overlay) close();
	});
	// iOS: impedisce che lo swipe sullo sfondo scorra la pagina sotto
	overlay.addEventListener(
		'touchmove',
		(e) => {
			if (e.target === overlay) e.preventDefault();
		},
		{ passive: false }
	);
};

const open = (trigger) => {
	const source = parseSource(trigger);
	if (!source || isOpen) return;

	isOpen = true;
	lastFocus = document.activeElement;

	const poster = trigger.getAttribute('data-poster');
	if (poster) video.setAttribute('poster', poster);
	else video.removeAttribute('poster');

	video.setAttribute('src', source.src);
	video.load();

	pauseScrolling(true);
	overlay.style.display = 'flex';
	overlay.setAttribute('aria-hidden', 'false');
	// forza il reflow prima della transizione di opacità
	void overlay.offsetWidth;
	overlay.classList.add('is-open');

	document.addEventListener('keydown', onKeydown);
	closeButton.focus();

	// play() dentro il click dell'utente: consentito con audio anche su iOS.
	// Nei browser vecchi play() non restituisce una Promise.
	const playing = video.play();
	if (playing && typeof playing.catch === 'function') {
		playing.catch(() => {
			// autoplay rifiutato: restano i controlli per avviarlo a mano
		});
	}
};

const close = () => {
	if (!isOpen) return;
	isOpen = false;

	video.pause();
	// interrompe il download del file
	video.removeAttribute('src');
	video.load();

	overlay.classList.remove('is-open');
	overlay.setAttribute('aria-hidden', 'true');
	overlay.style.display = 'none';

	document.removeEventListener('keydown', onKeydown);
	pauseScrolling(false);

	if (lastFocus && lastFocus.focus) lastFocus.focus();
};

const findTrigger = (target) =>
	target && target.closest ? target.closest('.js-video-lightbox') : null;

export default function initVideoLightbox(protectVideo) {
	if (!document.querySelector('.js-video-lightbox')) return;

	build(protectVideo);

	Array.prototype.forEach.call(document.querySelectorAll('.js-video-lightbox'), (trigger) => {
		if (trigger.tagName === 'A' && !trigger.hasAttribute('href')) {
			trigger.setAttribute('role', 'button');
			trigger.setAttribute('tabindex', '0');
		}
	});

	// delega sul document (in capture, prima di fullpage/swiper): funziona anche
	// sui pulsanti clonati da fullpage.js o aggiunti dopo l'init
	document.addEventListener(
		'click',
		(e) => {
			const trigger = findTrigger(e.target);
			if (!trigger) return;
			e.preventDefault();
			e.stopPropagation();
			open(trigger);
		},
		true
	);

	document.addEventListener('keydown', (e) => {
		if (isOpen) return;
		if (e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 32) {
			const trigger = findTrigger(e.target);
			if (!trigger) return;
			e.preventDefault();
			open(trigger);
		}
	});
}
